import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Typography } from '@mui/material';
import { FormSelect } from 'components/molecules/FormSelect';
import { FormModal } from 'components/molecules/Modal';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useQuery } from 'hooks/useQuery';
import { useTranslations } from 'hooks/useTranslations';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { IFormDeactivationLEValues } from 'models/legalEntity';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { useEffect } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

interface IActivationDeactivationLEProps {
  openLEActivation: boolean;
  handleCloseLEActivation: () => void;
  openLEDeactivation: boolean;
  handleCloseDeactivation: () => void;
  legalEntityUpdate: () => void;
  version: string;
}

export const ActivationDeactivationLE = ({
  openLEActivation,
  handleCloseLEActivation,
  openLEDeactivation,
  handleCloseDeactivation,
  legalEntityUpdate,
  version = '0',
}: IActivationDeactivationLEProps) => {
  const { t } = useTranslations();
  const query = useQuery();
  const { id = '' } = useParams<{ id: string }>();
  const type: string = query.get('type')?.trim() || '';
  const { selectedLanguage } = useLang();

  const {
    loadDictionaries,
    legalIndividualDeactivationReason,
    legalOrganisationDeactivationReason,
    deactivateLegalEntity,
    activateLegalEntity,
  } = useLegalEntities();

  const schema = yup.object({
    reasonUniqueId: yup.string().required(t('general.field.validation.required')),
  });

  useEffect(() => {
    (async function init() {
      if (type === ELegalEntityTypes.individual) {
        await loadDictionaries([EDictionaryTypes.legalIndividualDeactivationReason]);
      } else {
        await loadDictionaries([EDictionaryTypes.legalOrganisationDeactivationReason]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage, type]);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<IFormDeactivationLEValues>({
    defaultValues: { reasonUniqueId: null, version },
    resolver: yupResolver(schema) as unknown as Resolver<IFormDeactivationLEValues>,
  });

  const onClickDeactivate = async (data: any) => {
    if (data) {
      const result = await deactivateLegalEntity(id, type, data);
      if (result) {
        await legalEntityUpdate();
      }
      reset({ reasonUniqueId: null, version });
      handleCloseDeactivation();
    }
  };
  const onClickActivate = async () => {
    const result = await activateLegalEntity(id, type, { version });
    if (result) {
      await legalEntityUpdate();
    }
    handleCloseLEActivation();
  };

  return (
    <div>
      <FormModal
        open={openLEDeactivation}
        onClickClose={() => {
          handleCloseDeactivation();
          reset({ reasonUniqueId: null, version });
        }}
        approveButtonLabel="general.ok.label"
        title={t('legalEntityDetails.deactivateLegalEntity.label')}
        titleLeft
        approveButtonSubmit
        onSubmit={handleSubmit(onClickDeactivate)}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('legalEntityDetails.deactivateLegalEntity.pleaseSelectReason.text')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormSelect
              options={mapDictionaryToOptionProp(
                type === ELegalEntityTypes.individual
                  ? legalIndividualDeactivationReason
                  : legalOrganisationDeactivationReason,
              )}
              name="reasonUniqueId"
              label={t('legalEntityDetails.reason.label')}
              control={control}
              errors={errors}
              setValue={setValue}
              required
            />
          </Grid>
        </Grid>
      </FormModal>
      <FormModal
        open={openLEActivation}
        onClickClose={handleCloseLEActivation}
        onClickApprove={onClickActivate}
        approveButtonLabel="general.yes.label"
        cancelButtonLabel="general.no.label"
        title={t('legalEntityDetails.activateLegalEntity.label')}
        titleLeft
      >
        <Grid sx={{ gap: 4 }}>
          <Typography variant="body2">
            {t('legalEntityDetails.activateLegalEntity.willBeActive.text')}
          </Typography>
        </Grid>
      </FormModal>
    </div>
  );
};
